import {
  getDisabledLearnerRouteFallbackPath,
  isLearnerLegacyScreenEnabled,
} from '@app/routes/learner-feature-availability'
import {
  buildLearningAudioPath,
  buildLearningEbookLessonsPath,
  buildLearningEbookReaderPath,
  learnerPaths,
} from '@app/routes/route-paths'
import type {
  DashboardBook,
  DashboardUserProfile,
} from '@services/firebase'
import type { LearningLesson } from '@services/firebase/learning'
import { getFullLessons } from '@services/firebase/self-study'
import {
  mapLegacySelfStudyLessons,
  type LegacySelfStudyLesson,
} from './legacy-self-study-mapper'

export interface LegacyDashboardLessonsPayload {
  readonly lessons: LearningLesson[]
  readonly mappedLessons: readonly LegacySelfStudyLesson[]
}

export interface PreparedLegacyDashboardLessonsPayload
  extends LegacyDashboardLessonsPayload {
  readonly progressSource: 'chapter-summary-or-profile'
}

interface LegacyDashboardLessonDataPayload {
  readonly lessons: LearningLesson[]
}

interface LegacyDashboardLessonDataCacheEntry {
  readonly createdAt: number
  readonly payloadPromise: Promise<LegacyDashboardLessonDataPayload>
}

const LEGACY_LESSON_DATA_CACHE_TTL_MS = 5 * 60 * 1000
const legacyLessonDataCache = new Map<string, LegacyDashboardLessonDataCacheEntry>()

export function getLegacyScreenPath(
  screen: number,
  bookId: string | null | undefined,
  options?: { readonly preserveCurrentSearch?: boolean },
) {
  if (!isLearnerLegacyScreenEnabled(screen)) {
    return getDisabledLearnerRouteFallbackPath('short-videos') ?? learnerPaths.learning
  }

  switch (screen) {
    case 1:
      return learnerPaths.profile
    case 2:
      return learnerPaths.settings
    case 3:
      return learnerPaths.flashcards
    case 4:
      return learnerPaths.shortVideos
    case 5:
      return learnerPaths.teachers
    case 6:
      return learnerPaths.premium
    case 7:
      return learnerPaths.learningListeningQuiz
    case 8:
      return buildLearningEbookLessonsPath(bookId, options)
    case 9:
      return buildLearningEbookReaderPath(bookId, options)
    case 10:
      return buildLearningAudioPath(bookId)
    default:
      return learnerPaths.learning
  }
}

export function getCurrentProgramBook(
  books: DashboardBook[],
  currentProgram?: string,
) {
  if (!currentProgram) {
    return books[0] ?? null
  }

  return books.find((book) => book.id === currentProgram) ?? books[0] ?? null
}

async function loadLegacyLessonDataForBook(
  book: DashboardBook,
): Promise<LegacyDashboardLessonDataPayload> {
  const lessons = await getFullLessons(book.id)

  return {
    lessons,
  }
}

async function getCachedLegacyLessonDataForBook(
  book: DashboardBook,
): Promise<LegacyDashboardLessonDataPayload> {
  const cached = legacyLessonDataCache.get(book.id)
  const now = Date.now()

  if (cached && now - cached.createdAt < LEGACY_LESSON_DATA_CACHE_TTL_MS) {
    return cached.payloadPromise
  }

  const payloadPromise = loadLegacyLessonDataForBook(book)
  const entry = {
    createdAt: now,
    payloadPromise,
  }
  legacyLessonDataCache.set(book.id, entry)

  void payloadPromise.catch(() => {
      if (legacyLessonDataCache.get(book.id)?.payloadPromise === payloadPromise) {
        legacyLessonDataCache.delete(book.id)
      }
    })

  return payloadPromise
}

export async function prepareLegacyLessonsForBook(
  book: DashboardBook | null,
  profile: DashboardUserProfile,
): Promise<PreparedLegacyDashboardLessonsPayload> {
  const { lessons } = book
    ? await getCachedLegacyLessonDataForBook(book)
    : {
        lessons: [],
      }
  const mappedLessons = mapLegacySelfStudyLessons({
    lessons,
    profile,
  })

  return {
    lessons,
    mappedLessons,
    progressSource: 'chapter-summary-or-profile',
  }
}

export async function loadLegacyLessonsForBook(
  book: DashboardBook | null,
  profile: DashboardUserProfile,
): Promise<LegacyDashboardLessonsPayload> {
  const preparedLessons = await prepareLegacyLessonsForBook(book, profile)

  return {
    lessons: preparedLessons.lessons,
    mappedLessons: preparedLessons.mappedLessons,
  }
}
